import LoginModal from "@/components/modals/LoginModal";
import ApprovedSubmissions from "@/components/project-submission-admin-interface/ApprovedSubmissions";
import SubmissionReview from "@/components/project-submission-admin-interface/SubmissionReview";
import { SessionContext } from "@/context/SessionContext";
import { Box, Card, Tabs, Text, Title, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconLock } from "@tabler/icons-react";
import React, {
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";

const ProjectSubmissionAdminInterface: React.FC = () => {
	// r - Review, a - Approved
	const [activeTab, setActiveTab] = useState<string>("r");
	const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
	const [userHasAccess, setUserHasAccess] = useState<boolean>(false);

	const theme = useMantineTheme();
	const session = useContext(SessionContext);
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzLarge = isMobile ? 20 : 30;
	const fzActiveTab = isMobile ? 18 : 20;
	const fzInactiveTab = isMobile ? 18 : 15;

	const dashboardRef = useRef<HTMLDivElement>(null);

	if (!session) {
		return null;
	}

	useEffect(() => {
		if (session.user?.roles.includes("ConductionAdmin") || session.user?.roles.includes("InstrumentalistLiaison")) {
			setUserHasAccess(true);
			setShowLoginModal(false);
		} else {
			setUserHasAccess(false);
			if (session.user == null) {
				setShowLoginModal(true);
			}
		}
	}, [session.user]);

	if (!userHasAccess) {
		return (
			<>
				<LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
				<Card p={20}>
					<IconLock size={48} color={theme.colors.red[6]} />
					<Text>You do not have permission to access this page.</Text>
					<Text>Please log into an account with access or <Link to="/">click here to navigate away</Link>.</Text>
				</Card>
			</>
		);
	}

	return (
		<Box
			ref={dashboardRef}
			w="100%"
			h="100%"
			// Hard height boundary for the whole page section
			style={{
				display: "grid",
				gridTemplateRows: "auto 1fr",
				overflow: "hidden",
				minHeight: 0,
			}}
		>
			<Title
				p="xs"
				size={fzLarge}
				c={theme.colors.blue[0]}
				ta="center"
			>
				Project Submissions
			</Title>
			
			{/* Grid row 2: Tabs must consume remaining height and not overflow */}
			<Tabs
				value={activeTab}
				onChange={(value) => setActiveTab(value || "r")}
				w="98%"
				keepMounted
				pb={20}
				// Make Tabs itself a grid so List is auto and Panels get 1fr
				style={{
					justifySelf: "center",
					alignSelf: "stretch",
					display: "grid",
					gridTemplateRows: "auto 1fr",
					overflow: "hidden",
					minHeight: 0,
				}}
			>
				<Tabs.List>
					<Tabs.Tab
						h={50}
						w={isMobile ? "50%" : 150}
						value="r"
						bg={activeTab === "r" ? theme.colors.blue[8] : theme.colors.blue[3]}
						c={theme.colors.blue[8]}
						fw={700}
						fz={activeTab === "r" ? fzActiveTab : fzInactiveTab}
						bd="none"
						style={{
							"&:hover": {
								backgroundColor:
									activeTab === "r" ? theme.colors.blue[8] : theme.colors.blue[4],
							},
						}}
					>
						<Text
							fz={activeTab === "r" ? fzActiveTab : fzInactiveTab}
							c={activeTab === "r" ? theme.colors.blue[0] : theme.colors.blue[8]}
						>
							Review
						</Text>
					</Tabs.Tab>

					<Tabs.Tab
						h={50}
						w={isMobile ? "50%" : 230}
						value="a"
						bg={activeTab === "a" ? theme.colors.blue[8] : theme.colors.blue[3]}
						c={theme.colors.blue[8]}
						bd="none"
						fw={700}
						style={{
							"&:hover": {
								backgroundColor:
									activeTab === "a" ? theme.colors.blue[8] : theme.colors.blue[4],
							},
						}}
					>
						<Text
							fz={activeTab === "a" ? fzActiveTab : fzInactiveTab}
							c={activeTab === "a" ? theme.colors.blue[0] : theme.colors.blue[8]}
						>
							Approved
						</Text>
					</Tabs.Tab>
				</Tabs.List>

				{/* REVIEW panel */}
				<Tabs.Panel
					value="r"
					// Make the panel itself a height boundary
					style={{
						borderRadius: "0px 0px 15px 15px",
						overflow: "hidden",
						background: theme.colors.blue[8],
						padding: 15,
						minHeight: 0,
						display: "grid",
						gridTemplateRows: "1fr", // its child fills available space
					}}
				>
					{/* Wrapper ensures child can't escape the panel */}
					<Box
						h="100%"
						style={{ overflow: "hidden", minHeight: 0 }}
					>
						<SubmissionReview />
					</Box>
				</Tabs.Panel>

				{/* APPROVED panel */}
				<Tabs.Panel
					value="a"
					style={{
						borderRadius: "0px 0px 15px 15px",
						overflow: "hidden",
						background: theme.colors.blue[8],
						padding: 15,
						minHeight: 0,
						display: "grid",
						gridTemplateRows: "1fr",
					}}
				>
					<Box
						h="100%"
						style={{ overflow: "hidden", minHeight: 0 }}
					>
						<ApprovedSubmissions />
					</Box>
				</Tabs.Panel>
			</Tabs>
		</Box>
	);
};

export default ProjectSubmissionAdminInterface;
